import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Save, Printer, FileDown, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { DocEditor } from "../DocEditor";
import { Letterhead } from "../Letterhead";
import { ContractAI } from "./ContractAI";
import { ContractSendBox } from "./ContractSendBox";
import { ContractUploadSign } from "./ContractUploadSign";
import { contractsApi } from "../../lib/contractsApi";
import { printContract, contractPDFBlob } from "../../lib/contractPrint";
import { fileUrl } from "../../lib/api";
import { saveBlob } from "../../lib/download";
import { useAuth } from "../../context/AuthContext";

const FIELDS = [["party_name", "اسم الطرف الثاني"], ["party_id_number", "الهوية / السجل التجاري"], ["party_phone", "جوال الطرف الثاني"], ["party_email", "بريده (اختياري)"]];
const EMPTY = { title: "", type: "general", party_name: "", party_phone: "", party_email: "", party_id_number: "", start_date: "", end_date: "", value: "", body_html: "", notes: "" };

export const ContractEditor = ({ initial, types, onBack, onSaved }) => {
  const { company } = useAuth();
  const [c, setC] = useState(initial?.id ? initial : { ...EMPTY, ...initial });
  const [busy, setBusy] = useState("");
  const ed = useRef(null);
  useEffect(() => { if (initial?.id) contractsApi.get(initial.id).then((x) => { setC(x); ed.current?.setHTML(x.body_html || ""); }); }, [initial?.id]);
  const locked = c.status === "signed";
  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const payload = () => ({ ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, c[k] ?? ""])), value: c.value === "" || c.value == null ? null : Number(c.value), body_html: ed.current?.getHTML() || "" });
  const save = async () => {
    setBusy("save");
    try {
      const r = c.id ? await contractsApi.update(c.id, payload()) : await contractsApi.create(payload());
      setC((p) => ({ ...p, ...r })); onSaved(); toast.success("تم حفظ العقد");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(""); }
  };
  const doc = () => ({ ...c, body_html: ed.current?.getHTML() || c.body_html });
  const pdf = async () => {
    setBusy("pdf");
    try { await saveBlob(await contractPDFBlob(doc(), company, fileUrl), `${c.number || "عقد"}.pdf`, "application/pdf"); }
    catch { toast.error("تعذّر إنشاء PDF"); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-3" data-testid="contract-editor">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1" data-testid="contract-back"><ArrowRight className="h-4 w-4" /> الأرشيف</Button>
        <span className="text-xs text-slate-400 tabular-nums">{c.number}</span>
        {locked && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-xs font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> موقّع من {c.signed?.name}</span>}
        <div className="ms-auto flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" className="gap-1" onClick={() => printContract(doc(), company, fileUrl)} data-testid="contract-print"><Printer className="h-4 w-4" /> طباعة</Button>
          <Button size="sm" variant="outline" className="gap-1" onClick={pdf} disabled={busy === "pdf"} data-testid="contract-pdf">{busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF</Button>
          {!locked && <Button size="sm" className="gap-1 bg-blue-600 hover:bg-blue-700" onClick={save} disabled={busy === "save"} data-testid="contract-save">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ</Button>}
        </div>
      </div>
      <div className="grid lg:grid-cols-[1fr_320px] gap-3 items-start">
        <div className="space-y-3 min-w-0">
          <div className="rounded-2xl border bg-white p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <Input value={c.title} onChange={(e) => set("title", e.target.value)} placeholder="عنوان العقد" disabled={locked} className="sm:col-span-2" data-testid="contract-title" />
            <select value={c.type} onChange={(e) => set("type", e.target.value)} disabled={locked} className="h-10 rounded-md border px-2 text-sm bg-white" data-testid="contract-type">
              {Object.entries(types).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <Input type="number" value={c.value ?? ""} onChange={(e) => set("value", e.target.value)} placeholder="قيمة العقد (ريال)" disabled={locked} data-testid="contract-value" />
            {FIELDS.map(([k, l]) => <Input key={k} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={l} disabled={locked} data-testid={`contract-${k}`} />)}
            <label className="text-xs text-slate-500">من<Input type="date" value={c.start_date || ""} onChange={(e) => set("start_date", e.target.value)} disabled={locked} data-testid="contract-start" /></label>
            <label className="text-xs text-slate-500">إلى<Input type="date" value={c.end_date || ""} onChange={(e) => set("end_date", e.target.value)} disabled={locked} data-testid="contract-end" /></label>
          </div>
          <div className={locked ? "pointer-events-none opacity-95" : ""}>
            <DocEditor ref={ed} initialHTML={c.body_html || ""} header={<Letterhead company={company} />}
              footer={<div className="mt-10 grid grid-cols-2 gap-6 text-center text-xs text-slate-500"><div><b className="text-slate-700">الطرف الأول</b><div className="h-14 border-b mt-2" /></div><div><b className="text-slate-700">الطرف الثاني: {c.party_name}</b><div className="h-14 border-b mt-2" /></div></div>} />
          </div>
        </div>
        <div className="space-y-3 lg:sticky lg:top-2">
          {!locked && <ContractAI form={c} getBody={() => ed.current?.getHTML() || ""} onApply={(h) => ed.current?.setHTML(h)} />}
          <ContractSendBox contract={c} onChange={(x) => { setC(x); onSaved(); }} />
          <ContractUploadSign contract={c} onChange={(x) => { setC(x); onSaved(); }} />
        </div>
      </div>
    </div>
  );
};
